import api from '../api/axios';
import { ScheduleSession, RuleFrequency } from '../components/schedule/SessionDetailPanel';

// Frontend payload — matches the shape used in WeeklySchedule.tsx call sites.
export type SchedulePersistPayload = Omit<ScheduleSession, 'id' | 'lane' | 'serverId'>;

export interface ScheduleExceptionPayload {
  scheduleItemServerId: number;
  exceptionDate: string;          // YYYY-MM-DD
  status: 'cancelled' | 'modified';
  overrideStartTime?: string;
  overrideEndTime?: string;
  teacherIds?: number[];
  locationIds?: number[];
  reason?: string;
}

/**
 * Sparse update to an existing exception row. Only the keys actually present
 * are sent, matching the API's `'field' in body` semantics — so passing
 * `overrideStartTime: null` clears the override, while omitting it leaves the
 * stored value alone.
 */
export interface ScheduleExceptionUpdatePayload {
  status?: 'cancelled' | 'modified';
  overrideStartTime?: string | null;
  overrideEndTime?: string | null;
  teacherIds?: number[];
  locationIds?: number[];
  reason?: string | null;
}

/** One (rule, date) pair targeted by a bulk operation. */
export interface BulkOccurrence {
  scheduleItemServerId: number;
  date: string;                   // YYYY-MM-DD
}

export interface BulkCancelResult {
  /** Rows this call wrote — freshly cancelled plus converted. Drives Undo. */
  exceptionIds: number[];
  created: number;
  /** Dates whose existing one-off override was replaced by the cancellation. */
  converted: number;
  /** Already cancelled beforehand; left untouched, and not in exceptionIds. */
  alreadyCancelled: number;
  /** Refused because attendance is recorded against them. */
  skippedAttendance: BulkOccurrence[];
}

// ─── Field-name + value mapping (frontend ↔ API) ─────────────────────────────

// Application convention: 0 = Monday. Matches DAY_OFFSETS in WeeklySchedule.tsx.
const DAY_NAME_TO_INDEX: Record<string, number> = {
  Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3,
  Friday: 4, Saturday: 5, Sunday: 6,
};

// Map the front-end frequency to the API's repeat_interval enum.
// Note: 'once' has no DB equivalent (the schema CHECK only allows
// daily/weekly/bi-weekly/monthly), so we represent it as a weekly rule
// whose end_date equals its start_date — i.e. it occurs exactly once.
function mapFrequencyToInterval(
  freq: RuleFrequency,
): 'daily' | 'weekly' | 'bi-weekly' | 'monthly' {
  switch (freq) {
    case 'biweekly': return 'bi-weekly';
    case 'monthly':  return 'monthly';
    case 'once':     return 'weekly';
    case 'weekly':
    default:         return 'weekly';
  }
}

interface ApiCreateScheduleItem {
  child_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  start_date: string;
  end_date?: string | null;
  repeat_interval?: 'daily' | 'weekly' | 'bi-weekly' | 'monthly';
  is_active?: boolean;
  teacher_ids?: number[];
  classroom_ids?: number[];
}

function toCreatePayload(p: SchedulePersistPayload): ApiCreateScheduleItem {
  if (p.childId == null) {
    throw new Error('createScheduleItem requires a child_id (a child must be selected)');
  }
  const dayIndex = DAY_NAME_TO_INDEX[p.dayOfWeek];
  if (dayIndex === undefined) {
    throw new Error(`Unknown day name: ${p.dayOfWeek}`);
  }
  // 'once' → weekly rule pinned to a single date.
  const endDate = p.frequency === 'once' ? p.startDate : p.endDate;
  return {
    child_id: p.childId,
    day_of_week: dayIndex,
    start_time: p.startTime,
    end_time: p.endTime,
    start_date: p.startDate,
    end_date: endDate ?? null,
    repeat_interval: mapFrequencyToInterval(p.frequency),
    teacher_ids: p.teacherIds,
    classroom_ids: p.locationIds,
  };
}

// PATCH bodies are sparse — only the fields present in `updates` are mapped.
// child_id IS patchable, but only while the rule has no attendance history
// (the API returns 409 otherwise). A null childId is never sent — the column
// is NOT NULL, and an unassigned child only exists on unpersisted sessions.
function toUpdatePayload(updates: Partial<SchedulePersistPayload>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if ('childId'     in updates && updates.childId != null) {
    body.child_id = updates.childId;
  }
  if ('dayOfWeek'   in updates && updates.dayOfWeek !== undefined) {
    const idx = DAY_NAME_TO_INDEX[updates.dayOfWeek];
    if (idx === undefined) throw new Error(`Unknown day name: ${updates.dayOfWeek}`);
    body.day_of_week = idx;
  }
  if ('startTime'   in updates) body.start_time      = updates.startTime;
  if ('endTime'     in updates) body.end_time        = updates.endTime;
  if ('startDate'   in updates) body.start_date      = updates.startDate;
  if ('endDate'     in updates) body.end_date        = updates.endDate;
  if ('frequency'   in updates && updates.frequency !== undefined) {
    body.repeat_interval = mapFrequencyToInterval(updates.frequency);
    // 'once' on update: also pin end_date to start_date if not already supplied.
    if (updates.frequency === 'once' && body.end_date === undefined && updates.startDate) {
      body.end_date = updates.startDate;
    }
  }
  if ('teacherIds'  in updates) body.teacher_ids     = updates.teacherIds;
  if ('locationIds' in updates) body.classroom_ids   = updates.locationIds;
  return body;
}

interface ApiCreateScheduleException {
  schedule_item_id: number;
  exception_date: string;
  status: 'cancelled' | 'modified';
  override_start_time?: string | null;
  override_end_time?: string | null;
  reason?: string | null;
  teacher_ids?: number[];
  classroom_ids?: number[];
}

function toExceptionPayload(p: ScheduleExceptionPayload): ApiCreateScheduleException {
  return {
    schedule_item_id: p.scheduleItemServerId,
    exception_date: p.exceptionDate,
    status: p.status,
    override_start_time: p.overrideStartTime ?? null,
    override_end_time: p.overrideEndTime ?? null,
    reason: p.reason ?? null,
    teacher_ids: p.teacherIds,
    classroom_ids: p.locationIds,
  };
}

// ─── Hydration mappers (API → frontend shapes) ───────────────────────────────

/** Frontend-side shape for a hydrated rule (no synthetic local id / lane yet). */
export type HydratedScheduleItem = SchedulePersistPayload & {
  serverId: number;
};

export interface HydratedScheduleException {
  serverId: number;
  scheduleItemServerId: number;
  date: string;            // YYYY-MM-DD
  status: 'cancelled' | 'modified';
  // Override fields populated for status === 'modified'. Times come back as
  // 'HH:MM' (already normalised). Bridge arrays may be empty.
  overrideStartTime?: string;
  overrideEndTime?: string;
  teacherIds?: number[];
  locationIds?: number[];
  // Free-text reason — used by 'cancelled' to surface "Bank holiday" etc.
  reason?: string | null;
}

const INDEX_TO_DAY_NAME = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday', 'Sunday',
];

function indexToDayName(idx: number): string {
  return INDEX_TO_DAY_NAME[idx] ?? 'Monday';
}

function intervalToFrequency(interval: string): RuleFrequency {
  switch (interval) {
    case 'bi-weekly': return 'biweekly';
    case 'monthly':   return 'monthly';
    // 'daily' has no equivalent in the frontend grid (per deferred design
    // decisions); callers should filter daily rules out before mapping.
    case 'weekly':
    default:          return 'weekly';
  }
}

// PG returns DATE as ISO timestamp ('YYYY-MM-DDT00:00:00.000Z') after JSON
// serialisation, and TIME as 'HH:MM:SS'. Normalise for the frontend.
function isoToDateOnly(v: unknown): string | null {
  if (typeof v !== 'string' || v.length < 10) return null;
  return v.substring(0, 10);
}
function timeToHHMM(v: unknown): string {
  if (typeof v !== 'string') return '';
  return v.substring(0, 5);
}

function rowToHydratedItem(row: any): HydratedScheduleItem | null {
  if (row.repeat_interval === 'daily') {
    // Daily rules don't fit the weekly grid — skip silently. Documented in
    // SCHEDULE_EDITING_UX.md "Scope decisions deliberately deferred".
    // eslint-disable-next-line no-console
    console.warn('[scheduleDataService] skipping daily rule on hydration', row.id);
    return null;
  }
  const startDate = isoToDateOnly(row.start_date);
  if (!startDate) return null;
  const endDate = isoToDateOnly(row.end_date);
  // 'once' is stored as a weekly rule pinned to a single date (end_date ===
  // start_date), since the DB CHECK constraint disallows a 'once' interval.
  // Reverse the mapping here so the editor reads it back as one-off.
  const isOnce = row.repeat_interval === 'weekly' && endDate === startDate;
  return {
    serverId: row.id,
    dayOfWeek: indexToDayName(row.day_of_week),
    startTime: timeToHHMM(row.start_time),
    endTime: timeToHHMM(row.end_time),
    childId: row.child_id ?? null,
    teacherIds: Array.isArray(row.teacher_ids) ? [...row.teacher_ids] : [],
    locationIds: Array.isArray(row.classroom_ids) ? [...row.classroom_ids] : [],
    frequency: isOnce ? 'once' : intervalToFrequency(row.repeat_interval),
    startDate,
    endDate,
  };
}

function rowToHydratedException(row: any): HydratedScheduleException | null {
  const date = isoToDateOnly(row.exception_date);
  if (!date) return null;
  const start = row.override_start_time ? timeToHHMM(row.override_start_time) : undefined;
  const end = row.override_end_time ? timeToHHMM(row.override_end_time) : undefined;
  return {
    serverId: row.id,
    scheduleItemServerId: row.schedule_item_id,
    date,
    status: row.status,
    overrideStartTime: start,
    overrideEndTime: end,
    teacherIds: Array.isArray(row.teacher_ids) ? [...row.teacher_ids] : undefined,
    locationIds: Array.isArray(row.classroom_ids) ? [...row.classroom_ids] : undefined,
    reason: row.reason ?? null,
  };
}

// ─── Service ─────────────────────────────────────────────────────────────────

export class ScheduleDataService {
  /** Create a new recurring rule. Returns the server-assigned id. */
  static async createScheduleItem(payload: SchedulePersistPayload): Promise<{ id: number }> {
    const body = toCreatePayload(payload);
    const response = await api.post('/schedule/items', body);
    return { id: response.data.id };
  }

  /** Patch fields on an existing rule (live-update for fresh sessions, or
   *  any partial settled-rule mutation). */
  static async updateScheduleItem(
    serverId: number,
    updates: Partial<SchedulePersistPayload>,
  ): Promise<void> {
    const body = toUpdatePayload(updates);
    if (Object.keys(body).length === 0) return;
    await api.patch(`/schedule/items/${serverId}`, body);
  }

  /** End-date an existing rule (used when the user picks "From now on" or
   *  "Delete from this date forwards"). */
  static async endDateScheduleItem(serverId: number, endDate: string): Promise<void> {
    await api.patch(`/schedule/items/${serverId}`, { end_date: endDate });
  }

  /** Hard-delete a rule. Backend will return 409 if attendance/exceptions
   *  reference it; in that case prefer `endDateScheduleItem`. */
  static async deleteScheduleItem(serverId: number): Promise<void> {
    await api.delete(`/schedule/items/${serverId}`);
  }

  /** Write a one-off override for a single occurrence of a rule. */
  static async createScheduleException(
    payload: ScheduleExceptionPayload,
  ): Promise<{ id: number }> {
    const body = toExceptionPayload(payload);
    const response = await api.post('/schedule/exceptions', body);
    return { id: response.data.id };
  }

  /**
   * Patch an existing exception in place.
   *
   * data.schedule_exception is UNIQUE on (schedule_item_id, exception_date),
   * so a date can hold exactly one exception whatever its status. Re-overriding
   * a date that already has one MUST come through here — a second POST hits the
   * constraint and comes back 409.
   */
  static async updateScheduleException(
    exceptionServerId: number,
    updates: ScheduleExceptionUpdatePayload,
  ): Promise<void> {
    const body: Record<string, unknown> = {};
    if (updates.status !== undefined)     body.status               = updates.status;
    if ('overrideStartTime' in updates)   body.override_start_time  = updates.overrideStartTime ?? null;
    if ('overrideEndTime'   in updates)   body.override_end_time    = updates.overrideEndTime ?? null;
    if (updates.teacherIds  !== undefined) body.teacher_ids         = updates.teacherIds;
    if (updates.locationIds !== undefined) body.classroom_ids       = updates.locationIds;
    if ('reason' in updates)              body.reason               = updates.reason ?? null;
    if (Object.keys(body).length === 0) return;
    await api.patch(`/schedule/exceptions/${exceptionServerId}`, body);
  }

  /** Remove an exception by id — used to "uncancel" a previously cancelled date. */
  static async deleteScheduleException(exceptionServerId: number): Promise<void> {
    await api.delete(`/schedule/exceptions/${exceptionServerId}`);
  }

  /** Hydrate all currently-active rules (org-scoped server-side). */
  static async listScheduleItems(): Promise<HydratedScheduleItem[]> {
    const response = await api.get('/schedule/items');
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map(rowToHydratedItem)
      .filter((x: HydratedScheduleItem | null): x is HydratedScheduleItem => x !== null);
  }

  /**
   * Cancel many occurrences in one transaction. Used by the bulk-period
   * modal, where a term-long closure is hundreds of rows and a per-row
   * loop would be slow and non-atomic.
   */
  static async bulkCancelOccurrences(
    occurrences: BulkOccurrence[],
    reason: string,
  ): Promise<BulkCancelResult> {
    const response = await api.post('/schedule/exceptions/bulk', {
      occurrences: occurrences.map(o => ({
        schedule_item_id: o.scheduleItemServerId,
        exception_date: o.date,
      })),
      reason,
    });
    const d = response.data ?? {};
    return {
      exceptionIds: Array.isArray(d.exception_ids) ? d.exception_ids : [],
      created: d.created ?? 0,
      converted: d.converted ?? 0,
      alreadyCancelled: d.already_cancelled ?? 0,
      skippedAttendance: (Array.isArray(d.skipped_attendance) ? d.skipped_attendance : []).map(
        (r: any) => ({ scheduleItemServerId: r.schedule_item_id, date: r.exception_date }),
      ),
    };
  }

  /**
   * Delete many exceptions by id — the restore ("un-cancel") path, and the
   * modal's Undo. Ids that no longer exist are ignored server-side.
   */
  static async bulkDeleteExceptions(exceptionIds: number[]): Promise<{ deleted: number }> {
    if (exceptionIds.length === 0) return { deleted: 0 };
    const response = await api.post('/schedule/exceptions/bulk-delete', {
      exception_ids: exceptionIds,
    });
    return { deleted: response.data?.deleted ?? 0 };
  }

  /** Hydrate all schedule exceptions (org-scoped server-side). */
  static async listScheduleExceptions(): Promise<HydratedScheduleException[]> {
    const response = await api.get('/schedule/exceptions');
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map(rowToHydratedException)
      .filter(
        (x: HydratedScheduleException | null): x is HydratedScheduleException => x !== null,
      );
  }
}
